// Lib
import { useCallback, useEffect, useRef, useState } from 'react';
import { delay } from 'lodash';

// Utils
import {
    hasProgressiveImageFailed,
    hasProgressiveImageLoaded,
    markProgressiveImageAsFailed,
    markProgressiveImageAsLoaded,
} from './progressiveImagesSingleton';
import { useSelector } from 'react-redux';
import { getPlatformDetailsSelector } from '../../../platform/platformSelector';
import { isPlatformCapacitor } from '../../../platform/utils/platformDetailsUtils';
import { fetchImageObjectUrl } from '../../../element/image/imageFetchUtils';

const IMAGE_FADE_TIMEOUT = 300;

/**
 * Shows an image which progressively becomes better quality by first showing a thumbnail of the image,
 * and then, once the larger version has loaded, replaces the thumbnail with the full size image.
 *
 * NOTE: The component initially shows the source file because it would otherwise cause a flash of content when dragging
 * an element that has already loaded.
 * Now there will be a slight flash when the image has not yet loaded, but that's an acceptable trade off.
 */
export default ({ source: imageSourceUrl, onImageLoadCb }) => {
    const imageRef = useRef();
    const currentImageSourceUrlRef = useRef(imageSourceUrl);
    const capacitorBlobUrlRef = useRef(null);
    const hasUnmountedRef = useRef(false);

    const platformDetails = useSelector(getPlatformDetailsSelector);
    const isCapacitorApp = isPlatformCapacitor(platformDetails);

    const [renderedImageUrl, setRenderedImageUrl] = useState(isCapacitorApp ? null : imageSourceUrl);
    const [hasLoaded, setHasLoaded] = useState(isCapacitorApp ? false : hasProgressiveImageLoaded(imageSourceUrl));
    const [isError, setIsError] = useState(hasProgressiveImageFailed(imageSourceUrl));

    const callImageLoadCb = useCallback((imageToRenderUrl, onImageLoadFn) => {
        // Make sure the transparent fade has finished before switching the image.
        delay(() => onImageLoadFn && onImageLoadFn(imageToRenderUrl, imageRef.current), IMAGE_FADE_TIMEOUT);
    }, []);

    const setLoadedState = useCallback(
        (progressiveImageSourceUrl, imageToRenderUrl, onImageLoadFn) => {
            setIsError(false);

            // If this is the first time we've loaded this image source, call the load callback.
            if (!hasProgressiveImageLoaded(progressiveImageSourceUrl)) {
                callImageLoadCb(imageToRenderUrl, onImageLoadFn);
            }

            setRenderedImageUrl(imageToRenderUrl);
            setHasLoaded(true);
        },
        [callImageLoadCb],
    );

    const revokeObjectUrl = useCallback(() => {
        if (!capacitorBlobUrlRef.current) return;

        URL.revokeObjectURL(capacitorBlobUrlRef.current);
        capacitorBlobUrlRef.current = null;
    }, []);

    const fetchThenShowImage = useCallback(
        async (newImageSourceUrl, onImageLoadFn) => {
            let requestBlobUrl = null;

            const onLoad = (imageToRenderUrl) => {
                if (hasUnmountedRef.current) return;

                // Only show this image if it still matches the latest requested image source.
                // Otherwise a slower stale request can overwrite the current image.
                if (currentImageSourceUrlRef.current === newImageSourceUrl) {
                    setLoadedState(newImageSourceUrl, imageToRenderUrl, onImageLoadFn);
                }

                // Mark the original image source as loaded even if a newer request is now current.
                markProgressiveImageAsLoaded({ source: newImageSourceUrl });
            };

            const onError = () => {
                if (hasUnmountedRef.current) return;

                if (requestBlobUrl) URL.revokeObjectURL(requestBlobUrl);

                if (capacitorBlobUrlRef.current === requestBlobUrl) {
                    capacitorBlobUrlRef.current = null;
                }

                if (currentImageSourceUrlRef.current !== newImageSourceUrl) return;

                setIsError(true);

                // Treat an error as a completed load so callers can finish their loading state.
                callImageLoadCb(newImageSourceUrl, onImageLoadFn);

                markProgressiveImageAsFailed({ source: newImageSourceUrl });
            };

            try {
                // On web, don't refetch an image source that is already loaded for the current request.
                if (
                    !isCapacitorApp &&
                    currentImageSourceUrlRef.current === newImageSourceUrl &&
                    hasProgressiveImageLoaded(newImageSourceUrl)
                )
                    return;

                // Track the latest requested image source so stale async work can be ignored.
                currentImageSourceUrlRef.current = newImageSourceUrl;

                revokeObjectUrl();

                let imageToRenderUrl = newImageSourceUrl;

                if (isCapacitorApp) {
                    const { objectUrl } = await fetchImageObjectUrl(newImageSourceUrl);

                    // If a newer image source was requested, or the component unmounted while fetching,
                    // revoke this blob URL and ignore the stale result.
                    if (currentImageSourceUrlRef.current !== newImageSourceUrl || hasUnmountedRef.current) {
                        URL.revokeObjectURL(objectUrl);
                        return;
                    }

                    requestBlobUrl = objectUrl;
                    capacitorBlobUrlRef.current = objectUrl;
                    imageToRenderUrl = objectUrl;
                }

                // Create an in-memory image element and assign the resolved image URL so it loads in the background.
                imageRef.current = new Image();
                // Apparently the onLoad event won't fire if it's added to the image AFTER the src is set and
                // the image is loaded from the cache, but if you add it before it WILL.
                // Unfortunately, my empirical evidence suggests that's not the case in Chrome, but I'll add it
                // in this order anyway
                imageRef.current.onload = () => onLoad(imageToRenderUrl);
                imageRef.current.onerror = onError;
                imageRef.current.src = imageToRenderUrl;

                // If the image is loaded from cache, then it will be "complete" immediately, so trigger onLoad manually.
                // I've again seen inconsistent results with this in Chrome, but overall it seems to work.
                if (imageRef.current.complete) onLoad(imageToRenderUrl);
            } catch {
                onError();
            }
        },
        [callImageLoadCb, isCapacitorApp, revokeObjectUrl, setLoadedState],
    );

    useEffect(() => {
        hasUnmountedRef.current = false;

        // Remove any onload callbacks on un-mount
        return () => {
            hasUnmountedRef.current = true;

            if (imageRef.current) {
                imageRef.current.onload = () => null;
                imageRef.current.onerror = () => null;
            }

            revokeObjectUrl();
        };
    }, []);

    // When the requested image source changes, fetch the new image in the background.
    useEffect(() => {
        // If the requested image source is already loaded, don't fetch it again.
        // e.g. dropping an existing image element onto a link replace the preview
        if (!isCapacitorApp && hasProgressiveImageLoaded(imageSourceUrl)) {
            currentImageSourceUrlRef.current = imageSourceUrl;
            setLoadedState(imageSourceUrl, imageSourceUrl, onImageLoadCb);
            return;
        }

        setHasLoaded(false);
        void fetchThenShowImage(imageSourceUrl, onImageLoadCb);
    }, [fetchThenShowImage, isCapacitorApp, onImageLoadCb, setLoadedState, imageSourceUrl]);

    return {
        hideImage: !hasLoaded,
        visibleSource: renderedImageUrl,
        isError,
    };
};
